import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { 
  Mail,
  Inbox,
  CheckCircle,
  XCircle,
  Filter,
  Loader2,
  Eye,
  Phone,
  Calendar,
  MessageSquare,
  Trash2,
  AlertCircle,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  spam: { label: 'Spam', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
};

// Inquiry Detail Modal
const InquiryDetailModal = ({ inquiry, isOpen, onClose }) => {
  if (!isOpen || !inquiry) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-semibold text-white">{inquiry.name}</h2>
            <p className="text-slate-400">{inquiry.email}</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <User className="w-3 h-3" /> Name
              </p>
              <p className="text-white">{inquiry.name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Mail className="w-3 h-3" /> Email
              </p>
              <p className="text-white">{inquiry.email}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Phone className="w-3 h-3" /> Phone
              </p>
              <p className="text-white">{inquiry.phone || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase">Status</p>
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${STATUS_CONFIG[inquiry.status]?.color}`}>
                {STATUS_CONFIG[inquiry.status]?.label}
              </span>
            </div>
          </div>

          {/* Date */}
          <div className="pt-4 border-t border-slate-700">
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Received
              </p>
              <p className="text-slate-300 text-sm">{new Date(inquiry.created_at).toLocaleString()}</p>
            </div>
          </div>

          {/* Message */}
          <div className="pt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase flex items-center gap-1 mb-2">
              <MessageSquare className="w-3 h-3" /> Message
            </p>
            <div className="bg-slate-900/50 rounded-lg p-4">
              <p className="text-slate-300 text-sm whitespace-pre-wrap">
                {inquiry.message}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <p className="text-xs text-slate-500 text-center">
            Inquiry ID: {inquiry.id}
          </p>
        </div>
      </div>
    </div>
  );
};

const AdminInquiries = () => {
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, resolved: 0 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Action loading states
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/inquiries?${params}`);
      const data = response.data;
      setInquiries(data);
      
      // Calculate stats
      const allResponse = await axios.get(`${API_URL}/api/admin/inquiries`);
      const allData = allResponse.data;
      setStats({
        total: allData.length,
        pending: allData.filter(i => i.status === 'pending').length,
        resolved: allData.filter(i => i.status === 'resolved').length
      });
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleStatusChange = async (inquiryId, newStatus) => {
    setUpdatingId(inquiryId);
    try {
      await axios.put(`${API_URL}/api/admin/inquiries/${inquiryId}/status?status=${newStatus}`);
      fetchInquiries();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (inquiryId, name) => {
    if (!window.confirm(`Are you sure you want to delete the inquiry from "${name}"?`)) return;
    
    setDeletingId(inquiryId);
    try {
      await axios.delete(`${API_URL}/api/admin/inquiries/${inquiryId}`);
      fetchInquiries();
    } catch (error) {
      console.error('Error deleting inquiry:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowModal(true);
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-slate-600/50 transition-all">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      {/* Inquiry Detail Modal */}
      <InquiryDetailModal
        inquiry={selectedInquiry}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2" data-testid="inquiries-title">
          Inquiries
        </h1>
        <p className="text-slate-400">
          View and manage contact form submissions
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard 
          icon={Inbox} 
          label="Total Inquiries" 
          value={stats.total} 
          color="bg-slate-500/20 text-slate-400" 
        />
        <StatCard 
          icon={AlertCircle} 
          label="Pending" 
          value={stats.pending} 
          color="bg-amber-500/20 text-amber-400" 
        />
        <StatCard 
          icon={CheckCircle} 
          label="Resolved" 
          value={stats.resolved} 
          color="bg-emerald-500/20 text-emerald-400" 
        />
      </div>

      {/* Filters Row */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-slate-500" />
          <select
            data-testid="inquiries-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Inquiries Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No inquiries found</p>
            <p className="text-slate-500 text-sm mt-1">
              {statusFilter ? 'Try adjusting your filter' : 'Contact form submissions will appear here'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> Name
                    </div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone
                    </div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Message
                    </div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Created
                    </div>
                  </th>
                  <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {inquiries.map((inquiry) => (
                  <tr key={inquiry.id} className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-medium text-white">{inquiry.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-300">{inquiry.email}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400">{inquiry.phone || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 truncate block max-w-[200px]" title={inquiry.message}>
                        {inquiry.message.length > 50 ? `${inquiry.message.substring(0, 50)}...` : inquiry.message}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        {updatingId === inquiry.id ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                            <span className="text-slate-400 text-sm">Updating...</span>
                          </div>
                        ) : (
                          <select
                            value={inquiry.status}
                            onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                            data-testid={`status-select-${inquiry.id}`}
                            className={`px-2 py-1 text-xs font-medium rounded border bg-transparent cursor-pointer focus:outline-none focus:ring-1 focus:ring-amber-500 ${STATUS_CONFIG[inquiry.status]?.color}`}
                          >
                            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                              <option key={value} value={value} className="bg-slate-800 text-white">
                                {config.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 text-sm">
                        {new Date(inquiry.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewInquiry(inquiry)}
                          data-testid={`view-inquiry-${inquiry.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleDelete(inquiry.id, inquiry.name)}
                          disabled={deletingId === inquiry.id}
                          data-testid={`delete-inquiry-${inquiry.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm disabled:opacity-50"
                        >
                          {deletingId === inquiry.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminInquiries;
